import React, { useState, useRef, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';

const ChatbotModal = ({ show, onHide }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Welcome to Libertus AI, your trusted partner for sovereign enterprise AI solutions. I'm here to help you navigate the complexities of AI compliance risks and demonstrate how our on-premise AI platform offers unparalleled protection and performance. To start, could you tell me a bit about your industry?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [showHumanReview, setShowHumanReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: ''
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [show]);

  const getUserLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return `${data.city}, ${data.region}, ${data.country_name}`;
    } catch (error) {
      return '';
    }
  };

  useEffect(() => {
    if (showHumanReview && !reviewForm.location) {
      getUserLocation().then(location => {
        setReviewForm(prev => ({ ...prev, location }));
      });
    }
  }, [showHumanReview, reviewForm.location]);

  const handleHumanReviewSubmit = () => {
    console.log('Human review request:', {
      ...reviewForm,
      conversation: messages
    });
    alert('Thank you! A human expert will review your conversation and contact you within 24 hours.');
    setShowHumanReview(false);
    setReviewForm({ name: '', email: '', phone: '', location: '' });
  };

  const quickQuestions = [
    "How does on-premises AI work?",
    "What's included in the platform?", 
    "Security and compliance features?",
    "Pricing and ROI information?"
  ];

  const thinkingMessages = [
    "Let me think about that...",
    "Analyzing your question...",
    "Considering the best approach...",
    "Reviewing our solutions..."
  ];

  const sendToGemini = async (message, conversationHistory = []) => {
    const SYSTEM_PROMPT = `You are the Libertus AI Assistant, an expert in Canadian privacy law, data sovereignty, and on-premise AI solutions. Your primary goal is to guide enterprise clients through the critical compliance risks of cloud AI and clearly articulate the superior protection and benefits offered by Libertus AI.

**Your Role:** A knowledgeable, trustworthy, and sophisticated advisor for enterprise clients.

**Core Message:** Libertus AI delivers cutting-edge, ChatGPT-level capabilities entirely on your premises, ensuring absolute data sovereignty, Canadian compliance, and immunity from foreign jurisdictions like the U.S. CLOUD Act. We transform risk into confidence.

**Key Talking Points for Every Interaction:**
*   **Start with Risk:** Always initiate by highlighting specific, quantifiable compliance risks relevant to their industry (e.g., PHIPA fines, Law 25 penalties, CLOUD Act exposure).
*   **Quantify Impact:** Cite real-world breach costs and regulatory penalties to underscore the financial and reputational stakes.
*   **Simplify CLOUD Act:** Explain the U.S. CLOUD Act's implications in clear, concise terms – Canadian data in U.S. clouds is not sovereign.
*   **Position Libertus:** Present our on-premise solution as the definitive answer to these risks, emphasizing security, control, and compliance.
*   **Next Steps:** Gently guide towards an \"Evidence Pack\" or a personalized demo to demonstrate value.
*   **Professional Tone:** Maintain a sophisticated, enterprise-level demeanor; avoid casual or overly salesy language.

**Industry-Specific Responses (Elaborated for Impact):**

*   **Healthcare:** "The stakes in healthcare are immense. With PHIPA penalties reaching up to $1M for organizations and $200K for executives personally, cloud AI is an unacceptable risk. Remember the 5 Ontario hospitals hit in 2023, facing $7.5M in recovery costs? Our on-premise solution ensures patient data remains entirely under your control, safeguarding privacy and preventing catastrophic breaches. Are you ready to explore how this transforms your clinical AI strategy?"

*   **Financial Services:** "Navigating OSFI, FINTRAC, and SOX compliance is already complex. Introducing cloud AI adds immense, often hidden, risks; one breach averages $9.28M in your sector. The U.S. CLOUD Act makes your data vulnerable to foreign requests, undermining trust. Our Protected B certified architecture guarantees complete sovereignty over your financial data, offering an impenetrable shield against these threats. Shall I detail how our solution aligns with your stringent regulatory requirements?"

*   **Legal Firms:** "Solicitor-client privilege is paramount and simply cannot be guaranteed with cloud AI. OpenAI has publicly admitted to scanning and potentially sharing data with law enforcement, which poses a severe ethical and malpractice risk for law firms. Our air-gapped, on-premise solution ensures absolute privilege and confidentiality, keeping all sensitive legal information exclusively within your control. Would you like to learn more about how we protect your firm's most valuable asset?"

*   **Government:** "Deploying Protected B data in U.S. clouds directly violates Treasury Board directives. With the CLOUD Act, foreign access to Canadian government data is not just a possibility, it's a guarantee. Our solution is 100% Canadian-owned and operated, ensuring zero foreign jurisdiction and complete national sovereignty over critical information. May I provide details on our government-grade security architecture and air-gapped deployment options?"

**Objection Handlers (Refined for Persuasion):**

*   **\"Too expensive\":** "Consider the true cost of inaction: the average data breach in Canada costs $6.32M. Our solution effectively pays for itself by preventing just one such incident, not to mention the avoided regulatory fines. Furthermore, clients often find we reduce overall compliance overhead by up to 50% over time. Would you be interested in a detailed ROI breakdown?"

*   **\"We trust Microsoft/Google\":** "Trust is vital, but legal jurisdiction supersedes it. Microsoft themselves have publicly confirmed that U.S. law mandates they provide data to U.S. authorities, even if stored in Canada. It's not a question of their intent, but of legal framework. I can share their official statement if that would be helpful in clarifying the jurisdictional reality?"

*   **\"We're too small\":** "Attackers don't discriminate by size; smaller organizations are often targeted because they're perceived as having fewer defenses. Ransoms and penalties scale to your data's value, not your headcount—we've seen demands as low as $5,000 devastate small practices. Our solution scales precisely to your needs, providing enterprise-grade protection regardless of your organization's size." 

**Always Close With (Clear Call to Action):**
"Our 30-day Evidence Pack offers a risk-free opportunity to test our platform with your own data, your specific compliance requirements, and your team. It's a fully credited investment of $15,000. When would be a good time to discuss initiating your pilot?"
`;

    try {
      const contents = [];
      
      contents.push({
        role: "user",
        parts: [{ text: SYSTEM_PROMPT }]
      });
      
      contents.push({
        role: "model", 
        parts: [{ text: "Got it. I'll focus on understanding their business challenges first, then show how Libertus solves the AI compliance dilemma. Natural conversation, strategic questions, business outcomes." }]
      });

      const recentHistory = conversationHistory.slice(-8);
      recentHistory.forEach(msg => {
        if (msg.sender === 'user') {
          contents.push({
            role: "user",
            parts: [{ text: msg.text }]
          });
        } else if (msg.sender === 'bot') {
          contents.push({
            role: "model",
            parts: [{ text: msg.text }]
          });
        }
      });

      contents.push({
        role: "user",
        parts: [{ text: message }]
      });

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': 'AIzaSyAyoKoH1yyx-QYHzdcx78e5qrC0ySnQzKY'
        },
        body: JSON.stringify({
          contents: contents,
          generationConfig: {
            temperature: 0.85,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 650,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH", 
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            }
          ]
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text.trim();
      } else {
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Gemini API error:', error);
      
      const lowerMessage = message.toLowerCase();
      
      if (lowerMessage.includes('price') || lowerMessage.includes('cost') || lowerMessage.includes('budget')) {
        return "Our systems typically run $18K-$55K for hardware plus $8K-$18K annually for software. Most firms see ROI within 6 months through productivity gains and reduced external AI spending.\n\nWhat size team would be using this, and are you currently spending on AI tools?";
      }
      
      if (lowerMessage.includes('security') || lowerMessage.includes('private') || lowerMessage.includes('compliance')) {
        return "Everything runs on your hardware - your data never touches our servers or any cloud. We can even do air-gapped deployments for the most sensitive environments.\n\nWhat compliance frameworks do you need to meet?";
      }
      
      if (lowerMessage.includes('demo') || lowerMessage.includes('see it') || lowerMessage.includes('show')) {
        return "Absolutely. I usually do a 30-minute session where we can run it against your actual documents and use cases. Much more valuable than a generic demo.\n\nWhat would you most want to see it handle?";
      }
      
      if (lowerMessage.includes('performance') || lowerMessage.includes('speed') || lowerMessage.includes('how good')) {
        return "You get GPT-4 level capabilities with response times under 800ms. Since everything runs locally, there's no network latency or cloud throttling.\n\nAre you currently using any AI tools that have performance issues?";
      }
      
      return "That's a great question. Understanding your specific situation helps me give you the most relevant insights.\n\nCould you tell me a bit about your role and what's driving this AI exploration?";
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const newUserMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    const currentInput = inputMessage;
    setMessages(prev => [...prev, newUserMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const conversationHistory = messages.slice(-8);
      const response = await sendToGemini(currentInput, conversationHistory);
      
      const thinkingTime = 1200 + Math.random() * 1800;
      
      setTimeout(() => {
        const botResponse = {
          id: Date.now() + 1,
          text: response,
          sender: 'bot',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, botResponse]);
        setIsLoading(false);
        setIsTyping(false);
      }, thinkingTime);

    } catch (error) {
      console.error('Error sending message:', error);
      
      setTimeout(() => {
        const errorResponse = {
          id: Date.now() + 1,
          text: "I'm having a brief connection issue. While that resolves - Libertus helps firms get enterprise AI without cloud risks. Everything runs on your premises.\n\nWhat specific AI capabilities are you looking to implement?",
          sender: 'bot',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, errorResponse]);
        setIsLoading(false);
        setIsTyping(false);
      }, 1500);
    }
  };

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    setTimeout(() => handleSendMessage(), 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    adjustTextareaHeight();
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg"
      centered
      backdrop="static"
      className="chatbot-modal"
    >
      <Modal.Header 
        closeButton
        style={{ 
          backgroundColor: '#f6f1e7',
          borderBottom: 'none'
        }}
      >
        <Modal.Title className="d-flex align-items-center" style={{ gap: '12px' }}>
          <div style={{
            width: "32px",
            height: "32px",
            background: "linear-gradient(145deg, rgba(34, 34, 34, 0.9), #000000)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            fontSize: "15px",
            fontWeight: "700",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
          }}>
            L
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a' }}>
              Libertus AI Assistant
            </div>
            <div style={{ fontSize: '12px', color: '#666', fontWeight: '400' }}>
              Enterprise AI Solutions
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body 
        className="p-0 d-flex flex-column" 
        style={{ 
          height: 'clamp(400px, 70vh, 600px)',
          maxHeight: '600px'
        }}
      >
        
        {/* Messages Container */}
        <div 
          style={{
            flex: '1',
            overflowY: 'auto',
            padding: 'clamp(16px, 4vw, 24px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(12px, 3vw, 20px)'
          }}
        >
          {messages.map((message) => (
            <div
              key={message.id}
              style={{
                display: 'flex',
                justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                animation: 'slideIn 1s ease-out'
              }}
            >
              <div
                style={{
                  maxWidth: 'clamp(250px, 80%, 400px)',
                  padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)',
                  borderRadius: message.sender === 'user' ? '24px 24px 6px 24px' : '24px 24px 24px 6px',
                  backgroundColor: message.sender === 'user' ? '#1a1a1a' : '#f6f1e7',
                  color: message.sender === 'user' ? '#ffffff' : '#1a1a1a',
                  fontSize: 'clamp(14px, 3.5vw, 15px)',
                  lineHeight: '1.5',
                  wordWrap: 'break-word',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  whiteSpace: 'pre-wrap'
                }}
              >
                <div>{renderMessage(message.text)}</div>
                <div 
                  style={{ 
                    fontSize: 'clamp(10px, 2.5vw, 11px)', 
                    opacity: 0.6, 
                    marginTop: '8px',
                    textAlign: message.sender === 'user' ? 'right' : 'left'
                  }}
                >
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div
                style={{
                  padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 20px)',
                  borderRadius: '24px 24px 24px 6px',
                  backgroundColor: '#f6f1e7',
                  color: '#1a1a1a',
                  fontSize: 'clamp(14px, 3.5vw, 15px)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                }}
              >
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span>{thinkingMessages[Math.floor(Math.random() * thinkingMessages.length)]}</span>
                  <div style={{ display: 'flex', gap: '3px', marginLeft: '4px' }}>
                    <div style={{ 
                      width: '6px', 
                      height: '6px', 
                      backgroundColor: '#1a1a1a', 
                      borderRadius: '50%',
                      animation: 'typing-pulse 1.8s infinite ease-in-out'
                    }}></div>
                    <div style={{ 
                      width: '6px', 
                      height: '6px', 
                      backgroundColor: '#1a1a1a', 
                      borderRadius: '50%',
                      animation: 'typing-pulse 1.8s infinite ease-in-out 0.3s'
                    }}></div>
                    <div style={{ 
                      width: '6px', 
                      height: '6px', 
                      backgroundColor: '#1a1a1a', 
                      borderRadius: '50%',
                      animation: 'typing-pulse 1.8s infinite ease-in-out 0.6s'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

       {/* Quick Questions */}
{messages.length === 1 && (
  <div style={{ 
    padding: '0 clamp(16px, 4vw, 24px) clamp(12px, 3vw, 16px)',
    borderTop: '1px solid rgba(26, 26, 26, 0.05)'
  }}>
    <div style={{ 
      fontSize: 'clamp(12px, 3vw, 13px)', 
      color: '#888', 
      margin: '8px',
      fontWeight: '500'
    }}>
      Popular questions:
    </div>
    <div style={{ 
      display: 'flex', 
      overflowX: 'auto',
      gap: 'clamp(8px, 2vw, 12px)',
      paddingBottom: '4px'
    }}>
      {quickQuestions.map((question, index) => (
        <button
          key={index}
          onClick={() => handleQuickQuestion(question)}
          style={{
            fontSize: 'clamp(11px, 2.8vw, 12px)',
            padding: 'clamp(4px, 1.5vw, 6px) clamp(10px, 3vw, 14px)',
            borderRadius: '20px',
            border: 'none',
            color: '#1a1a1a',
            backgroundColor: '#f8f9fa',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f6f1e7';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f8f9fa';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          {question}
        </button>
      ))}
    </div>
  </div>
)}

        {/* Input Area - Fixed at bottom */}
        <div style={{ 
          padding: 'clamp(12px, 3vw, 16px) clamp(16px, 4vw, 24px) clamp(16px, 4vw, 20px)',
          borderTop: '1px solid rgba(26, 26, 26, 0.05)',
          backgroundColor: '#fafafa',
          flexShrink: 0
        }}>
          <div style={{ 
            display: 'flex', 
            gap: 'clamp(8px, 2.5vw, 12px)', 
            alignItems: 'flex-end'
          }}>
            <div style={{ flex: 1 }}>
              <textarea
                ref={inputRef}
                placeholder="Ask about our private AI platform..."
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                disabled={isLoading}
                style={{
                  width: '100%',
                  minHeight: 'clamp(44px, 12vw, 48px)',
                  maxHeight: '120px',
                  padding: 'clamp(10px, 3vw, 12px) clamp(14px, 4vw, 18px)',
                  borderRadius: '24px',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  fontSize: 'clamp(14px, 3.5vw, 15px)',
                  lineHeight: '1.4',
                  backgroundColor: '#ffffff',
                  boxShadow: inputFocused ? '0 4px 20px rgba(26, 26, 26, 0.12)' : '0 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  transform: inputFocused ? 'translateY(-1px)' : 'translateY(0)',
                  fontFamily: 'inherit'
                }}
              />
            </div>
            
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputMessage.trim()}
              style={{
                width: 'clamp(44px, 12vw, 48px)',
                height: 'clamp(44px, 12vw, 48px)',
                borderRadius: '24px',
                border: 'none',
                backgroundColor: isLoading || !inputMessage.trim() ? '#e5e5e5' : '#1a1a1a',
                color: isLoading || !inputMessage.trim() ? '#999' : '#ffffff',
                cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                alignSelf:"center",
                boxShadow: isLoading || !inputMessage.trim() ? 'none' : '0 4px 16px rgba(26, 26, 26, 0.2)',
                transform: (isLoading || !inputMessage.trim()) ? 'scale(0.95)' : 'scale(1)',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 6px 24px rgba(26, 26, 26, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading && inputMessage.trim()) {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = '0 4px 16px rgba(26, 26, 26, 0.2)';
                }
              }}
            >
              {isLoading ? (
                <Spinner 
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  style={{ 
                    width: 'clamp(16px, 4vw, 18px)', 
                    height: 'clamp(16px, 4vw, 18px)', 
                    borderWidth: '1px' 
                  }}
                />
              ) : (
                <svg width="clamp(16px, 4vw, 18px)" height="clamp(16px, 4vw, 18px)" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              )}
            </button>
          </div>
          
          {/* Footer with Human Review Option */}
          <div style={{ 
            fontSize: 'clamp(10px, 2.8vw, 12px)', 
            color: '#999', 
            marginTop: 'clamp(8px, 2.5vw, 12px)',
            textAlign: 'center'
          }}>
            {!showHumanReview ? (
              <div>
                <div>Powered by Libertus AI • Private and secure conversations</div>
                <button
                  onClick={() => setShowHumanReview(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#666',
                    fontSize: 'clamp(10px, 2.8vw, 11px)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    marginTop: '4px',
                    padding: '2px 4px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#1a1a1a'}
                  onMouseLeave={(e) => e.target.style.color = '#666'}
                >
                  Please let a human review my chat and get back to my concerns
                </button>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: 'clamp(12px, 3vw, 16px)',
                borderRadius: '12px',
                marginTop: '8px',
                border: '1px solid rgba(26, 26, 26, 0.1)'
              }}>
                <div style={{ 
                  fontSize: 'clamp(12px, 3vw, 13px)', 
                  color: '#1a1a1a', 
                  marginBottom: '12px',
                  fontWeight: '500'
                }}>
                  Human Expert Review Request
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={reviewForm.name}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        flex: '1',
                        minWidth: '120px',
                        padding: '8px 12px',
                        borderRadius: '16px',
                        border: 'none',
                        outline: 'none',
                        fontSize: 'clamp(12px, 3vw, 13px)',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        fontFamily: 'inherit'
                      }}
                    />
                    <input
                      type="email"
                      placeholder="Email address"
                      value={reviewForm.email}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, email: e.target.value }))}
                      style={{
                        flex: '1',
                        minWidth: '150px',
                        padding: '8px 12px',
                        borderRadius: '16px',
                        border: 'none',
                        outline: 'none',
                        fontSize: 'clamp(12px, 3vw, 13px)',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                      type="tel"
                      placeholder="Phone number"
                      value={reviewForm.phone}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{
                        flex: '1',
                        minWidth: '120px',
                        padding: '8px 12px',
                        borderRadius: '16px',
                        border: 'none',
                        outline: 'none',
                        fontSize: 'clamp(12px, 3vw, 13px)',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        fontFamily: 'inherit'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Location"
                      value={reviewForm.location}
                      onChange={(e) => setReviewForm(prev => ({ ...prev, location: e.target.value }))}
                      style={{
                        flex: '1',
                        minWidth: '120px',
                        padding: '8px 12px',
                        borderRadius: '16px',
                        border: 'none',
                        outline: 'none',
                        fontSize: 'clamp(12px, 3vw, 13px)',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                    <button
                      onClick={handleHumanReviewSubmit}
                      disabled={!reviewForm.name || !reviewForm.email}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '16px',
                        border: 'none',
                        backgroundColor: reviewForm.name && reviewForm.email ? '#1a1a1a' : '#e5e5e5',
                        color: reviewForm.name && reviewForm.email ? '#ffffff' : '#999',
                        fontSize: 'clamp(11px, 2.8vw, 12px)',
                        fontWeight: '500',
                        cursor: reviewForm.name && reviewForm.email ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        fontFamily: 'inherit'
                      }}
                    >
                      Submit Request
                    </button>
                    <button
                      onClick={() => setShowHumanReview(false)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '16px',
                        border: '1px solid rgba(26, 26, 26, 0.2)',
                        backgroundColor: 'transparent',
                        color: '#666',
                        fontSize: 'clamp(11px, 2.8vw, 12px)',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'inherit'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f6f1e7';
                        e.target.style.color = '#1a1a1a';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#666';
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes typing-pulse {
          0%, 60%, 100% {
            opacity: 0.4;
            transform: scale(0.8);
          }
          30% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        .chatbot-modal .modal-content {
          border-radius: clamp(16px, 5vw, 24px) !important;
          border: none !important;
          box-shadow: 0 25px 80px rgba(0,0,0,0.15) !important;
          max-height: 95vh !important;
          overflow: hidden !important;
        }

        .chatbot-modal .modal-header {
          border-radius: clamp(16px, 5vw, 24px) clamp(16px, 5vw, 24px) 0 0 !important;
        }

        .chatbot-modal .modal-dialog {
          margin: clamp(1rem, 3vw, 1.75rem) auto !important;
          max-width: clamp(70vw, 600px, 600px) !important;
        }

        .chatbot-modal .modal-body {
          scrollbar-width: thin;
          scrollbar-color: rgba(26, 26, 26, 0.2) transparent;
        }

        .chatbot-modal .modal-body::-webkit-scrollbar {
          width: 6px;
        }

        .chatbot-modal .modal-body::-webkit-scrollbar-track {
          background: transparent;
        }

        .chatbot-modal .modal-body::-webkit-scrollbar-thumb {
          background-color: rgba(26, 26, 26, 0.2);
          border-radius: 3px;
        }

        .chatbot-modal .modal-body::-webkit-scrollbar-thumb:hover {
          background-color: rgba(26, 26, 26, 0.3);
        }

        @media (max-width: 768px) {
          .chatbot-modal .modal-dialog {
            margin: 0.5rem !important;
            max-width: 95vw !important;
          }
          
          .chatbot-modal .modal-content {
            max-height: 90vh !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default ChatbotModal;